// SinglePickerField provides a wrapper to support
// existing standalone SinglePicker instances in reportbuilder
// SinglePickerField receives value as a string.
//
// Before using this component, I recommend reviewing OptionPickerField
// since it supports a variety of single and multi-value use cases
// with props and sends/receives value as an object.
// OptionPickerField may be a better choice depending on your use case.
import PropTypes from 'prop-types';
import React from 'react';
import { v4 as uuid } from 'uuid';
import { kebabCase } from 'lodash';
import { enabledSelector, labelSelector, newPathSelector, optionsSelector, valueSelector } from '../selectors/index';
import entityComposition from '../../common/classes/entityComposition';
import Label from './label';
import SinglePicker from '../../common/controls/singlePicker';
import logValueOnChange from '../commonActions/logValueOnChange';
import { leafPedigreesString } from '../../../../behaviors/src/lib/common';
import { removeNulls } from '../../../utils/contentTools';
const SinglePickerField = _ref => {
  let _ref$allowsOther = _ref.allowsOther,
    allowsOther = _ref$allowsOther === void 0 ? false : _ref$allowsOther,
    _ref$className = _ref.className,
    className = _ref$className === void 0 ? '' : _ref$className,
    enabled = _ref.enabled,
    _ref$endMessage = _ref.endMessage,
    endMessage = _ref$endMessage === void 0 ? null : _ref$endMessage,
    _ref$dropdownIndicato = _ref.dropdownIndicatorGlyph,
    dropdownIndicatorGlyph = _ref$dropdownIndicato === void 0 ? 'chevron-down' : _ref$dropdownIndicato,
    _ref$label = _ref.label,
    label = _ref$label === void 0 ? null : _ref$label,
    _ref$labelKey = _ref.labelKey,
    labelKey = _ref$labelKey === void 0 ? 'title' : _ref$labelKey,
    onChange = _ref.onChange,
    _ref$onFocus = _ref.onFocus,
    onFocus = _ref$onFocus === void 0 ? () => null : _ref$onFocus,
    _ref$options = _ref.options,
    options = _ref$options === void 0 ? [] : _ref$options,
    _ref$placeholder = _ref.placeholder,
    placeholder = _ref$placeholder === void 0 ? '' : _ref$placeholder,
    _ref$value = _ref.value,
    value = _ref$value === void 0 ? '' : _ref$value,
    _ref$valueKey = _ref.valueKey,
    valueKey = _ref$valueKey === void 0 ? 'identifier' : _ref$valueKey,
    _ref$otherKey = _ref.otherKey,
    otherKey = _ref$otherKey === void 0 ? null : _ref$otherKey,
    path = _ref.path,
    _ref$onTextChange = _ref.onTextChange,
    onTextChange = _ref$onTextChange === void 0 ? null : _ref$onTextChange,
    _ref$isLoading = _ref.isLoading,
    isLoading = _ref$isLoading === void 0 ? false : _ref$isLoading,
    _ref$isSearching = _ref.isSearching,
    isSearching = _ref$isSearching === void 0 ? false : _ref$isSearching;
  const otherValueKey = otherKey || valueKey;

  /**
   * @method handleChange - if the new value is an "Other", set the `otherValueKey` ("identifier")
   * as a UUID
   */
  const handleChange = newValue => {
    const result = JSON.parse(JSON.stringify(newValue));
    if (allowsOther && result && result[valueKey]) {
      const optionsMatchingNewValue = options.filter(opt => opt[valueKey] === result[valueKey]);
      if (optionsMatchingNewValue.length === 0) {
        delete result[valueKey];
        result[otherValueKey] = uuid();
      }
    }
    const displayValue = leafPedigreesString(removeNulls(result));
    onChange({
      path,
      value: result,
      displayValue
    });
  };

  /**
   * @method prepareOptions - to satisfy the root `Select` component in `SinglePicker`, the value
   * prop must also be in the list of available options.
   */
  const prepareOptions = () => {
    let result = JSON.parse(JSON.stringify(options));
    if (allowsOther && value && value[valueKey]) {
      const optionsMatchingValue = options.filter(opt => opt[valueKey] === value[valueKey]);
      if (optionsMatchingValue.length === 0) {
        result = result.concat(value);
      }
    }
    return result;
  };
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Label, {
    value: label
  }), /*#__PURE__*/React.createElement(SinglePicker, {
    allowCreate: allowsOther,
    disabled: !enabled,
    dropdownIndicatorGlyph: dropdownIndicatorGlyph,
    className: "".concat(className, " t-spf-").concat(kebabCase(label || '')),
    labelKey: labelKey,
    onFocus: onFocus,
    onChange: handleChange,
    options: prepareOptions(),
    placeholder: placeholder,
    endMessage: endMessage,
    value: value,
    valueKey: valueKey,
    verticalPadding: false,
    onTextChange: onTextChange,
    isLoading: isLoading,
    isSearching: isSearching,
    path: path
  }));
};
SinglePickerField.propTypes = {
  allowsOther: PropTypes.bool,
  className: PropTypes.string,
  endMessage: PropTypes.string,
  enabled: PropTypes.bool.isRequired,
  path: PropTypes.string.isRequired,
  dropdownIndicatorGlyph: PropTypes.string,
  label: PropTypes.string,
  labelKey: PropTypes.string,
  otherKey: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  placeholder: PropTypes.string,
  isLoading: PropTypes.bool,
  isSearching: PropTypes.bool,
  onTextChange: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.string, PropTypes.shape({})]),
  valueKey: PropTypes.string
};
export { SinglePickerField as UncomposedSinglePickerField };
export default entityComposition(SinglePickerField, [], {
  enabled: enabledSelector,
  label: labelSelector,
  options: optionsSelector,
  value: valueSelector,
  onChange: logValueOnChange,
  path: newPathSelector
});